import { ISelectionChangeArgs, ISelectItem } from "@components/inputs/select/Select.types";
import { ICachedFieldState, IStorageSmartFieldValues } from "@odata/Data.utils";
import { getFieldInfo, IFieldInfo } from "@odata/FieldInfo.utils";
import { isDefined } from "@utils/general";
import React, { ComponentType } from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { AppContext, IAppContext } from "../../../contexts/appContext/AppContext.types";
import { FieldType, LabelStatus } from "../../../enums";
import { TRecordAny, TValue } from "../../../global.types";
import { IAuditTrailData, Model } from "../../../model/Model";
import { IValidationError } from "../../../model/Validator.types";
import BindingContext from "../../../odata/BindingContext";
import { FormStorage } from "../../../views/formView/FormStorage";
import { IMultiFieldSelectChange } from "../../inputs/businessPartnerSelect";
import { ICheckboxChange } from "../../inputs/checkbox";
import { ICheckboxGroupChange } from "../../inputs/checkboxGroup/CheckboxGroup";
import { ISmartCheckboxGroupChangeEvent } from "../../inputs/checkboxGroup/SmartCheckboxGroup";
import Field from "../../inputs/field";
import Input, {
    IInputOnBlurEvent,
    IInputOnChangeEvent,
    IInputRowsChangeEvent,
    ISharedInputProps
} from "../../inputs/input";
import { IMultiSelectionChangeArgs } from "../../inputs/select/MultiSelect";
import { TCellValue } from "../../table";
import { TooltipIconInfo } from "../../tooltipIcon";
import { StyledTooltipContent } from "../../tooltipIcon/TooltipIcon.styles";
import { getInfoValue, IFieldDef, IFieldDefFn, IFieldInfoProperties } from "../FieldInfo";
import { ISmartRadioGroupChange } from "../smartRadioButtonGroup/SmartRadioButtonGroup";
import { TFieldValue } from "../smartTable/SmartTable.utils";
import { IValueHelperChangeArgs } from "../smartValueHelper";
import {
    getControlData,
    getValueHelperChangeParams,
    IGetControlDataRetValue,
    ISmartFieldEvents
} from "./SmartFieldUtils";

export interface ISmartFieldTempDataActionArgs {
    bindingContext: BindingContext;
    groupId?: string;
}

export interface ISmartFieldChange {
    value?: TValue;
    parsedValue?: TValue;
    selectedItems?: ISelectItem[];
    currentValue?: string;
    groupId?: string;

    type?: FieldType;
    bindingContext: BindingContext;
    triggerAdditionalTasks?: boolean;
    additionalData?: TRecordAny;
    // time to time, we need to work with previous value of the field. Storage is responsible to set the previous value
    // to the event just before if updates it in the storage, so it's always available
    previousValue?: TValue;
}

export function isSmartFieldChangeEvent(obj: any): obj is ISmartFieldChange {
    return isDefined(obj) && (obj.value !== undefined || obj.triggerAdditionalTasks !== undefined);
}

export interface ISmartFieldWidthChange {
    width: string;
    bindingContext: BindingContext;
}

export interface ISmartFieldHeightChange {
    height: string;
    bindingContext: BindingContext;
}

export interface ISmartFieldBlur extends IInputOnBlurEvent {
    groupId?: string;
    bindingContext: BindingContext;
    isCreating?: boolean;
}

export interface ISmartFieldValidationError {
    groupId?: string;
    data: IValidationError;
    bindingContext: BindingContext;
}

export type TCustomRenderWrapper = (field: React.ReactNode, info: IFieldInfo, data: IGetControlDataRetValue) => React.ReactNode;
export type TFieldProps = ISharedInputProps & {
    labelStatus?: LabelStatus;
    hasPadding?: boolean;
    tooltip?: string;
    isRequired?: boolean;
    tokenColor?: string;
    isWithoutRightMargin?: boolean;
};

export interface ISmartFieldProps {
    bindingContext: BindingContext;
    storage: Model;
    uuid?: string;
    /** Use if SmartField is defined with custom Model or to override definition stored in storage fieldsInfo
     * for example if you want to use multiple SmartFields for same property with different formatter */
    fieldDef?: IFieldInfoProperties;

    fieldProps?: TFieldProps;

    isSkeleton?: boolean;

    className?: string;
    style?: React.CSSProperties;

    showLabelDescription?: boolean;
    useWidthWhenReadOnly?: boolean;
    renderReadOnlyAsDisabled?: boolean;

    fastEntryListId?: string;

    onChange?: (args: ISmartFieldChange) => void;
    onTemporalChange?: (args: ISmartFieldChange) => void;
    onCancel?: (args: ISmartFieldTempDataActionArgs) => void;
    onConfirm?: (args: ISmartFieldTempDataActionArgs) => void;

    // callback when field state has changed (become visible/hidden, become required or is not required anymore)
    onFieldStateChange?: (bc: BindingContext, state: ICachedFieldState, prevState: ICachedFieldState) => void;

    onBlur?: (args: ISmartFieldBlur) => Promise<void>;
    /** ResponsiveInput can cause width change event */
    onWidthChange?: (args: ISmartFieldWidthChange) => void;
    onHeightChange?: (args: ISmartFieldHeightChange) => void;

    // Method called to customize render
    customRenderWrapper?: TCustomRenderWrapper;
    // adds extra content to Field before Input
    extraFieldContent?: React.ReactElement;

    // otherwise doesn't work for typescript, because of withTranslation HOC
    ref?: React.Ref<React.ReactElement>;
}

interface ISmartFieldState {
    fieldInfo: IFieldInfo;
}

export interface ISmartFieldRenderProps {
    info?: IFieldInfo;
    formattedValue?: TCellValue | Promise<TCellValue> | TFieldValue;
    parentProps?: ISmartFieldProps;
    storageData?: IStorageSmartFieldValues;
    auditTrailData?: IAuditTrailData;
    onRowsChange?: ({ numRows, newHeight }: IInputRowsChangeEvent) => void;
    onBlur?: (args: IInputOnBlurEvent) => Promise<void>;
    error?: IValidationError;
    name?: string;
    width?: string;
    value?: any;
    checked?: boolean;
    currentValue?: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    isReadOnly?: boolean;
    isSharpLeft?: boolean;
    isSharpRight?: boolean;
    isVisible?: boolean;
    labelStatus?: LabelStatus;
    showChange?: boolean;
    // could be any of the change events handled by SmartField (input, select...)
    onChange?: (args: ISmartFieldChange) => void;
}

export class SmartField<T extends ISmartFieldProps = ISmartFieldProps> extends React.Component<T & WithTranslation, ISmartFieldState> {
    static contextType = AppContext;
    //sadly, breaks typescript type checking
    //context: React.ContextType<typeof AppContext>;

    state = {
        fieldInfo: null as IFieldInfo
    };

    _isMounted: boolean;
    _currentFieldState: ICachedFieldState;
    uuid: string;

    constructor(props: T & WithTranslation, context: IAppContext) {
        super(props, context);

        // automatically register SmartField on model
        this.props.storage.addRef(this, this.props.bindingContext);
    }

    get customRenderWrapper(): TCustomRenderWrapper {
        return this.props.customRenderWrapper;
    }

    get fieldProps(): TFieldProps {
        return this.props.fieldProps;
    }

    cacheCurrentFieldStateAndTriggerEvents(triggerEvents: boolean): void {
        const { storage, bindingContext } = this.props;

        const savedState = storage.getAdditionalFieldData(bindingContext, "currentFieldState");
        const savedStateKeys: (keyof ICachedFieldState)[] = ["isRequired", "isVisible"];
        if (savedState && savedStateKeys.some((key) => isDefined(savedState[key]) && savedState[key] !== this._currentFieldState[key])) {
            this.props.onFieldStateChange?.(bindingContext, this._currentFieldState, savedState);
        }

        storage.setAdditionalFieldData(bindingContext, "currentFieldState", { ...this._currentFieldState }, false);
    }

    componentDidMount() {
        this._isMounted = true;
        this.loadInfo();
        this.cacheCurrentFieldStateAndTriggerEvents(false);
    }

    componentDidUpdate(prevProps: T, prevState: ISmartFieldState): void {
        this.loadInfo();
        this.cacheCurrentFieldStateAndTriggerEvents(true);
        if (!prevProps.bindingContext.isSame(this.props.bindingContext)) {
            this.props.storage?.removeRef(this, prevProps.bindingContext);
            this.props.storage.addRef(this, this.props.bindingContext);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        const { storage, bindingContext } = this.props;
        storage?.removeRef(this, this.props.bindingContext);
        // remove cached field state while unmounting
        storage.setAdditionalFieldData(bindingContext, "currentFieldState", null, false);
    }

    // uncomment, if there is problem with unsynced skeletons in the form
    shouldComponentUpdate(nextProps: Readonly<T>): boolean {
        if (this.props.isSkeleton) {
            return false;
        }

        return true;
    }

    getInfo = () => {
        let fieldInfo = this.props.storage.getInfo(this.props.bindingContext);

        if (this.props.fieldDef) {
            fieldInfo = { ...fieldInfo, ...this.props.fieldDef };
        }

        return fieldInfo;
    };

    loadInfo = async () => {
        if (this.getInfo()) {
            return;
        }

        const info = await getFieldInfo({
            bindingContext: this.props.bindingContext,
            context: this.context,
            fieldDef: this.props.fieldDef as IFieldDef
        });

        if (info) {
            this.props.storage.data.fieldsInfo[this.props.bindingContext.getFullPath(true)] = info;
            this.forceUpdate();
        }
    };

    handleBlur = (args: IInputOnBlurEvent) => {
        return this.props.onBlur?.({
            // DEV-41507
            // this is potentially risky, if any issue occurs move it afterwards again and solve the issue with flag
            // case solved: SmartField contains multiple other SFs -> blurring one will overwrite BC of nested SF
            bindingContext: this.props.bindingContext,
            ...args
        });
    };

    handleFocus = () => {
        // do nothing
    };

    handleInputChange = (data: IInputOnChangeEvent) => {
        this.handleFieldChange({
            value: data.value,
            type: this.getControlType(),
            bindingContext: this.props.bindingContext
        });
    };

    handleColorChange = (value: string) => {
        this.handleFieldChange({
            value,
            type: this.getControlType(),
            bindingContext: this.props.bindingContext
        });
    };

    handleCheckboxChange = (args: ICheckboxChange) => {
        this.handleFieldChange({
            value: args.value,
            type: this.getControlType(),
            bindingContext: this.props.bindingContext
        });
    };

    handleSwitchChange = (checked: boolean) => {
        this.handleFieldChange({
            value: checked,
            parsedValue: checked,
            type: this.getInfo().type,
            bindingContext: this.props.bindingContext
        });
    };

    handleConfirm = () => {
        this.props.onConfirm?.({
            bindingContext: this.props.bindingContext
        });
    };

    handleCancel = () => {
        this.props.onCancel?.({
            bindingContext: this.props.bindingContext
        });
    };

    handleSelectChange = (args: ISelectionChangeArgs | IMultiFieldSelectChange | IMultiSelectionChangeArgs) => {
        this.handleFieldChange({
            value: args.value as TValue,
            selectedItems: (args as IMultiSelectionChangeArgs).selectedItems,
            additionalData: args.additionalData,
            triggerAdditionalTasks: args.triggerAdditionalTasks,
            type: this.getControlType(),
            bindingContext: this.props.bindingContext
        });
    };

    handleValueHelperChange = (args: IValueHelperChangeArgs) => {
        this.handleFieldChange(getValueHelperChangeParams(this.props.bindingContext, this.getControlType(), args));
    };

    handleSegmentedButtonChange = (key: string) => {
        this.handleFieldChange({
            value: key,
            type: FieldType.SegmentedButton,
            bindingContext: this.props.bindingContext,
            // to trigger correctEnumValue in FormStorage.handleChange
            triggerAdditionalTasks: true
        });
    };

    handleOnDateChange = (e: IInputOnChangeEvent) => {
        this.handleFieldChange({
            value: e.value,
            parsedValue: e.value,
            type: this.getControlType(),
            bindingContext: this.props.bindingContext,
            triggerAdditionalTasks: e.triggerAdditionalTasks
        });
    };

    handleFileChange = (args: ISmartFieldChange) => {
        this.handleFieldChange(args);
    };

    handleWidthChange = (width: string) => {
        this.props.onWidthChange?.({
            bindingContext: this.props.bindingContext,
            width
        });
    };

    handleRowsChange = ({ newHeight }: IInputRowsChangeEvent) => {
        this.props.onHeightChange?.({
            bindingContext: this.props.bindingContext,
            height: newHeight
        });
    };

    handleCheckboxGroupChange = (args: ICheckboxGroupChange) => {
        this.handleFieldChange({
            bindingContext: this.props.bindingContext,
            value: args.values,
            // FormStorage.storeMultiValue expects this
            triggerAdditionalTasks: true
        });
    };

    handleRadioButtonChange = (args: ISmartRadioGroupChange) => {
        this.handleFieldChange({
            bindingContext: this.props.bindingContext,
            value: args.value
        });
    };

    handleSmartCheckboxGroupChange = (args: ISmartCheckboxGroupChangeEvent) => {
        this.handleFieldChange({
            bindingContext: args.bindingContext,
            value: args.value
        });
    };

    handleFieldChange = (args: ISmartFieldChange) => {
        const isConfirmable = this.getInfo()?.isConfirmable;

        if (isConfirmable) {
            this.props.onTemporalChange?.(args);
        } else {
            (this.props.storage as FormStorage).handleFirstChange?.();
            this.props.onChange(args);
        }
    };

    getDisabledMessage = (): string => {
        // currently, we don't want to show automatic disabled message https://solitea-cz.atlassian.net/browse/DEV-18205
        return "";
    };

    getControlType = () => {
        return this.getInfo()?.type;
    };

    render() {
        const info = this.getInfo();
        if (!info) {
            return null;
        }

        const events: ISmartFieldEvents = {
            onBlur: this.handleBlur,
            onFocus: this.handleFocus,
            onDateChange: this.handleOnDateChange,
            onFileChange: this.handleFileChange,
            onSegmentedButtonChange: this.handleSegmentedButtonChange,
            onWidthChange: this.handleWidthChange,
            onInputChange: this.handleInputChange,
            onCheckboxChange: this.handleCheckboxChange,
            onColorChange: this.handleColorChange,
            onSwitchChange: this.handleSwitchChange,
            onCheckboxGroupChange: this.handleCheckboxGroupChange,
            onSmartRadioButtonChange: this.handleRadioButtonChange,
            onSmartCheckboxGroupChange: this.handleSmartCheckboxGroupChange,
            onRowsChange: this.handleRowsChange,
            onSelectChange: this.handleSelectChange,
            onValueHelperChange: this.handleValueHelperChange,
            onCancel: this.handleCancel,
            onConfirm: this.handleConfirm,
            onChange: this.props.onChange
        };

        const { storage, bindingContext, renderReadOnlyAsDisabled } = this.props;
        let { fieldProps } = this;
        const controlData = getControlData({
            bc: bindingContext,
            storage,
            type: this.getControlType(),
            context: this.context,
            fieldDef: this.props.fieldDef,
            events,
            fastEntryListId: this.props.fastEntryListId,
            isSkeleton: this.props.isSkeleton,
            renderReadOnlyAsDisabled
        });

        const { isRequired, isVisible } = controlData.storageData ?? {};
        this._currentFieldState = { isRequired, isVisible };

        if (!controlData?.storageData.isVisible) {
            return null;
        }

        let fieldElement;
        const auditTrailData = fieldProps?.auditTrailData ?? controlData.storageData.additionalFieldData?.auditTrailData;

        if (this.getControlType() !== FieldType.Custom) {
            const tooltip = fieldProps?.tooltip ?? (info?.tooltip && getInfoValue(info, "tooltip", {
                storage, info, bindingContext
            }));
            const extraFieldContent = this.props.isSkeleton ? null : getInfoValue(info, "extraFieldContent", {
                storage, info, bindingContext
            }) || this.props.extraFieldContent;
            const extraFieldContentAfter = this.props.isSkeleton ? null : getInfoValue(info, "extraFieldContentAfter", {
                storage, info, bindingContext
            });

            const tooltipAfter = this.props.isSkeleton ? null : getInfoValue(info, "tooltipAfter", {
                storage,
                info,
                bindingContext
            });
            const labelStatus = getInfoValue(info, "labelStatus", { storage });

            // Currently, we show sharpLeft input for fields with extra content before, usually text,
            // but we may need to customize this in fieldSettings in the future?
            fieldProps = {
                ...fieldProps,
                isSharpLeft: fieldProps?.isSharpLeft || !!extraFieldContent,
                isSynchronized: controlData.props.isSynchronized,
                ...(info?.fieldSettings?.overrideFieldProps ?? {}),
            };

            //@ts-ignore
            const Control = <controlData.Control
                {...controlData.props}
                {...fieldProps}
                key={controlData.props?.key}
            />;

            // the same thing could be done for checkbox/radiobutton HERE instead of in field
            fieldElement = (
                <Field
                    {...fieldProps}
                    className={this.props.className}
                    isFullRow={controlData.props.isFullRow}
                    style={this.props.style}
                    auditTrailData={auditTrailData}
                    key={this.uuid}
                    name={controlData.props.name}
                    isValueHelp={controlData.isValueHelp}
                    useWidthWhenReadOnly={this.props.useWidthWhenReadOnly}
                    tooltip={tooltip}
                    disabledMessage={controlData.props.disabledText ?? this.getDisabledMessage()}
                    textAlign={fieldProps?.textAlign ?? controlData.props.textAlign}
                    isRequired={fieldProps?.isRequired ?? controlData.props.isRequired}
                    isDisabled={fieldProps?.isDisabled ?? controlData.props.isDisabled}
                    isReadOnly={fieldProps?.isReadOnly ?? controlData.props.isReadOnly}
                    isSkeleton={this.props.isSkeleton}
                    type={this.getControlType()}
                    label={info?.fieldSettings?.checkBoxFieldLabel ?? info?.label}
                    description={(this.props.showLabelDescription || info?.showLabelDescription) && info?.description as string}
                    width={fieldProps?.width ?? controlData.props.width}
                    isWithoutRightMargin={fieldProps?.isWithoutRightMargin}
                    hasDependentFields={this.getInfo().customizationData?.dependents?.length > 0}
                    labelStatus={fieldProps?.labelStatus ?? labelStatus}>
                    {extraFieldContent}
                    {Control}
                    {extraFieldContentAfter}
                    {tooltipAfter && <TooltipIconInfo style={{ marginLeft: "5px" }}>
                        <StyledTooltipContent>{tooltipAfter}</StyledTooltipContent>
                    </TooltipIconInfo>}
                </Field>
            );
        }

        if (info.render) {
            const renderProps: IFieldDefFn = {
                storage: this.props.storage as Model,
                bindingContext,
                fieldElement,
                events,
                props: {
                    parentProps: this.props,
                    info,
                    storageData: controlData.storageData,
                    ...controlData.props,
                    ...fieldProps,
                    auditTrailData: auditTrailData
                }
            };

            if (this.props.isSkeleton) {
                if (info.renderSkeleton) {
                    fieldElement = info.renderSkeleton(renderProps);
                } else {
                    fieldElement = (
                        <Field {...fieldProps} isSkeleton>
                            <Input isSkeleton {...controlData.props}/>
                        </Field>
                    );
                }
            } else {
                fieldElement = info.render(renderProps);
            }

        }

        return this.customRenderWrapper ? this.customRenderWrapper(fieldElement, info, controlData) : fieldElement;
    }
}

const HOC = withTranslation(["Common", "Components"], { withRef: true })(SmartField) as ComponentType<ISmartFieldProps>;
HOC.displayName = "SmartFieldWithTranslation";

export default HOC;
